import { Typography } from "@shared/ui.primitives";
import React, { useEffect, useRef } from "react";
import type {
  FieldValues,
  SubmitHandler,
  UseFormReturn
} from "react-hook-form";
import { FormProvider } from "react-hook-form";

import { Notification } from "../../types/notification";
import { scrollToErrorMessage } from "../../utils/scrollToErrorMessage";
import { InfoBlock } from "../InfoBlock/InfoBlock";

import { errorMessageContent, errorWrapper } from "./Form.css";

export type OnSubmitFormType<T> = (data: T) => Promise<void>;

export type FormProps<T extends FieldValues> = {
  testId: string;
  methods: UseFormReturn<T>;
  onSubmitForm: SubmitHandler<T>;
  id?: string;
  className?: string;
  error: Notification | null;
  children: JSX.Element | JSX.Element[];
};

export interface InputProps {
  value: string | number | boolean;
  inputName: string;
  id?: string;
  autoComplete?: string;
  disabled?: boolean;
  label?: string;
  labelSecondary?: string;
  placeholder?: string;
  errorMessage?: string;
  onChange?: (eventOrValue: unknown) => void;
  onBlur?: (eventOrValue: unknown) => void;
  "data-testid"?: string;
  readOnly?: boolean;
}

export const Form = <T extends FieldValues>({
  testId: dataTestId,
  onSubmitForm,
  error: graphqlError = null,
  children,
  className,
  methods
}: FormProps<T>): JSX.Element => {
  const {
    handleSubmit,
    formState: { submitCount }
  } = methods;
  const formRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    scrollToErrorMessage(formRef, graphqlError);
  }, [submitCount, graphqlError]);

  useEffect(() => {
    if (formRef && formRef.current) {
      formRef.current.dataset.testRenderState = "complete";
    }
  }, []);

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    await handleSubmit(onSubmitForm)();
  };

  return (
    <FormProvider {...methods}>
      <form
        ref={formRef}
        data-testid={dataTestId}
        onSubmit={onSubmit}
        autoComplete="off"
        className={className}
      >
        {graphqlError && (
          <div className={errorWrapper}>
            <InfoBlock>
              <div
                data-testid="form-error-content"
                className={errorMessageContent}
              >
                <div>
                  <div>
                    <Typography variant="body-md-bold">
                      {graphqlError.message}
                    </Typography>
                  </div>
                  {graphqlError.detail && (
                    <div>
                      <Typography variant="body-md">
                        {graphqlError.detail}
                      </Typography>
                    </div>
                  )}
                </div>
                {graphqlError.onRetry && (
                  <button
                    type="button"
                    onClick={graphqlError.onRetry}
                    data-testid="retry-button"
                  >
                    Retry
                  </button>
                )}
              </div>
            </InfoBlock>
          </div>
        )}
        {children}
      </form>
    </FormProvider>
  );
};
